import React, { useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  ImageList,
  ImageListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Image from '~/components/Common/Image';

import { useGetRoomImages } from '~/hooks/apis/accommodation/useGetRoomImages';
import { useUpdateRoomImages } from '~/hooks/apis/accommodation/useUpdateRoomImages';

import { AdminRoomImageResponse } from '~/services/AccommodationService';

interface Props {
  roomId: string;
  roomName: string;
  isExpanded: boolean;
}

function compareRoomImages(left: AdminRoomImageResponse, right: AdminRoomImageResponse) {
  if (left.order === right.order) {
    return left.imageId.localeCompare(right.imageId);
  }

  return left.order - right.order;
}

function sortRoomImages(images: Array<AdminRoomImageResponse>) {
  return [...images].sort(compareRoomImages);
}

export default function AccommodationPropertyRoomImagesPanel({ roomId, roomName, isExpanded }: Props) {
  const { data, isLoading, error } = useGetRoomImages({
    roomId,
    enabled: isExpanded,
  });
  const updateRoomImagesMutation = useUpdateRoomImages();
  const [draftImages, setDraftImages] = useState<Array<AdminRoomImageResponse>>([]);
  const hasInitialisedDraftImagesRef = useRef(false);

  const sortedImages = useMemo(() => sortRoomImages(draftImages), [draftImages]);
  const sortedPersistedImages = useMemo(() => sortRoomImages(data?.images ?? []), [data?.images]);
  const hasChanges = !isEqual(sortedImages, sortedPersistedImages);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (!hasInitialisedDraftImagesRef.current || !hasChanges) {
      setDraftImages(data.images);
      hasInitialisedDraftImagesRef.current = true;
    }
  }, [data, hasChanges]);

  const handleTitleChange = (imageId: string, title: string) => {
    setDraftImages((currentImages) =>
      currentImages.map((image) =>
        image.imageId === imageId ? { ...image, title: title === '' ? null : title } : image,
      ),
    );
  };

  const handleSave = async () => {
    try {
      await updateRoomImagesMutation.mutateAsync({
        roomId,
        images: sortedImages.map((image) => ({
          imageId: image.imageId,
          title: image.title,
        })),
      });
    } catch {
      // Mutation-level error handling already surfaces feedback to the user.
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load room images: {error.message}</Alert>;
  }

  if (!data || sortedImages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No internal room images are currently stored for {roomName}.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {sortedImages.length} persisted room images.
        </Typography>

        <Button variant="contained" onClick={handleSave} disabled={!hasChanges || updateRoomImagesMutation.isPending}>
          {updateRoomImagesMutation.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </Stack>

      <ImageList cols={3} gap={16}>
        {sortedImages.map((image) => (
          <ImageListItem key={image.imageId}>
            <Stack
              spacing={1.5}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                }}
              >
                <Image
                  publicId={image.imageId}
                  alt={image.title ?? `${roomName} room image`}
                  options={{ width: 480, height: 320 }}
                  style={{ width: '100%', display: 'block' }}
                />
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Source: ${image.source}`} />
                <Chip size="small" label={`Order: ${image.order + 1}`} />
              </Stack>

              <TextField
                label="Title"
                value={image.title ?? ''}
                onChange={(event) => {
                  handleTitleChange(image.imageId, event.target.value);
                }}
                size="small"
                fullWidth
              />
            </Stack>
          </ImageListItem>
        ))}
      </ImageList>
    </Stack>
  );
}
